import { Pagination } from 'antd'
import Link from 'next/link'
import { ReactNode, useMemo } from 'react'
import { useAsync } from 'react-use'
import styled from 'styled-components'
import useBillboardMultiDimensionParams from '@/components/billboard/hooks/use-billboard-multi-dimension-params'
import useLinkListSortedCountries from '@/components/billboard/hooks/use-link-list-sorted-countries'
import useLinkListTitle from '@/components/billboard/hooks/use-link-list-title'
import Header from '@/components/billboard/link-list/header'
import LinkListLayout from '@/components/billboard/link-list/link-list-layout'
import Billboard from '@/components/billboard/top100/content/billboard'
import { parseCustomizedTagRouterName } from '@/components/billboard/utility/parse-customized-tag-router-name'
import { COUNTRY_I18N_MAP } from '@/constants/country-code'
import { CUSTOMIZED_TAG_I18N_MAP } from '@/constants/customized-tag'
import routes from '@/hooks/use-authorization/routes'
import { useI18n } from '@/hooks/use-i18n'
import { useIntl } from '@/i18n/hooks/use-intl'
import { NextLayoutComponentType } from '@/types/next-layout-component-type'
import { LinkListPlatforms } from '@/types/schema/billboard/multi-dimension'
import getPageData from '@/utils/get-page-data'

export const PAGE_SIZE = 40

export interface LinkListItem {
  title: string
  url: string
}

const CustomizedTagBillboardLinkList: NextLayoutComponentType = () => {
  const { formatMessage } = useIntl()
  const { locale } = useI18n()
  const countries = useLinkListSortedCountries()
  const { getLinkTitle } = useLinkListTitle()

  const { platform, platformTitle, country } =
    useBillboardMultiDimensionParams()
  const searchParams = routes.billboardCustomizedTagList.useSearchParams()
  const setSearchParams = routes.billboardCustomizedTagList.useSetSearchParams()

  const data = useMemo(() => {
    return countries.flatMap((country) => {
      return Object.keys(CUSTOMIZED_TAG_I18N_MAP).map((tag) => {
        const countryLocale = formatMessage({ id: COUNTRY_I18N_MAP[country] })
        const tagLocale = formatMessage({ id: CUSTOMIZED_TAG_I18N_MAP[tag] })
        const suffixLocale = formatMessage({
          id: 'landing:influencer_billboard',
        })

        return {
          title: getLinkTitle(platformTitle, [
            countryLocale,
            tagLocale,
            suffixLocale,
          ]),
          url: routes.billboardCustomizedTagDetail.stringifyUrl(
            {
              platform,
              country,
              tag: parseCustomizedTagRouterName(tag),
            },
            { lang: searchParams.data?.lang },
          ),
        }
      })
    })
  }, [
    countries,
    formatMessage,
    getLinkTitle,
    platformTitle,
    platform,
    searchParams.data?.lang,
  ])

  const linkList: LinkListItem[] = useMemo(() => {
    return getPageData(searchParams.data?.page ?? 1, data, PAGE_SIZE)
  }, [searchParams.data?.page, data])

  const changePage = async (page: number): Promise<void> => {
    await setSearchParams(
      {
        ...searchParams.data,
        page,
      },
      undefined,
      { shallow: false },
    )
  }

  const switchPlatform = async (platform: LinkListPlatforms): Promise<void> => {
    await routes.billboardCustomizedTagList.goto(
      {
        platform,
        country,
      },
      { ...searchParams.data, page: 1 },
    )
  }

  useAsync(async () => {
    await setSearchParams({
      ...searchParams.data,
      page: 1,
    })
  }, [locale])

  return (
    <Wrapper>
      <Header
        breadcrumbItems={[
          {
            title: `${platformTitle} ${formatMessage({
              id: 'general:navbar_item_search__kol_by_customized_tag',
            })}`,
          },
        ]}
        pageTitle={`${platformTitle} ${formatMessage({
          id: 'landing:influencer_billboard_category',
        })}`}
        onChangePlatform={switchPlatform}
      />
      <LinkListWrapper>
        {linkList.map((item) => (
          <Link href={item.url} key={item.title}>
            {item.title}
          </Link>
        ))}
      </LinkListWrapper>
      <Pagination
        current={searchParams.data?.page}
        pageSize={PAGE_SIZE}
        showSizeChanger={false}
        total={data.length}
        onChange={changePage}
      />
    </Wrapper>
  )
}

CustomizedTagBillboardLinkList.getLayout = (page): ReactNode => {
  return <LinkListLayout from='Billboard list'>{page}</LinkListLayout>
}

export default CustomizedTagBillboardLinkList

export const LinkListWrapper = styled.div<{ column?: number }>`
  display: grid;
  grid-template-columns: ${({ column }): string =>
    `repeat(${column ?? 4}, 1fr)`};
  gap: 30px 40px;

  a {
    color: ${({ theme }): string => theme.colors.text.primary};
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: 140%;
    text-decoration-line: underline;
    text-decoration-skip-ink: none;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(1, 1fr);
    gap: 24px;
    justify-items: center;

    a {
      font-size: 16px;
    }
  }
`

export const Wrapper = styled.div`
  margin: 80px auto;
  width: 100%;
  max-width: 1110px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;

  .ant-pagination {
    margin-top: 80px;

    a,
    .ant-pagination-item-container,
    .ant-pagination-item-ellipsis {
      line-height: unset;
    }
  }

  ${Billboard} {
    border-radius: 15px;
    box-shadow: 0 2px 12px 0 rgb(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    margin: 20px auto;
  }
`
